// eslint-disable-next-line skyscanner-dates/no-date-fns-format
import { addDays, format, parseISO } from 'date-fns';
import logger from 'saddlebag-logger';

import getDayviewUrl from '../HotelsSearchControlsGCWrapper/utils/url';

import type { InternalGCSearchParams } from '@skyscanner-internal/global-components/flights-search-controls/common/types/search';
import type { LocalisedURLStrings } from '@skyscanner-internal/global-components/flights-search-controls/utils/url-builder';

const EVERYWHERE = 'everywhere';

/**
 * Checks if a destination is a valid specific destination (not "Everywhere").
 * Following Banana's pattern for destination validation.
 */
export const hasDestination = (
  destination: { id?: string } | undefined,
): boolean =>
  !!destination && !!destination.id && destination.id !== EVERYWHERE;

const formatFlightDate = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  return `${year.slice(2)}${month}${day}`;
};

const buildFlightQueryParams = (
  searchParams: InternalGCSearchParams,
  returnDate: string | undefined,
): string[] => {
  const childrenParam =
    searchParams.child_ages && searchParams.child_ages.length > 0
      ? `childrenv2=${searchParams.child_ages.join('|')}`
      : null;

  const preferDirectsParam = searchParams.prefer_directs
    ? 'preferdirects=true'
    : null;

  const alternativeOriginsParam =
    searchParams.alternativeOrigins === false
      ? 'outboundaltsenabled=false'
      : null;

  const alternativeDestinationsParam =
    searchParams.alternativeDestinations === false
      ? 'inboundaltsenabled=false'
      : null;

  return [
    `adultsv2=${searchParams.adults || 1}`,
    `cabinclass=${searchParams.cabinClass ? searchParams.cabinClass.toLowerCase() : 'economy'}`,
    childrenParam,
    'ref=flights_seo',
    `rtn=${returnDate ? '1' : '0'}`,
    preferDirectsParam,
    alternativeOriginsParam,
    alternativeDestinationsParam,
  ].filter((param): param is string => param !== null);
};

/**
 * Builds a flight search URL from search parameters for parallel search functionality.
 *
 * URL formats:
 * - With destination: /{locale-transport}/{locale-flights}/{origin}/{destination}/{outbound_date}/{return_date}?{query_params}
 * - "Everywhere" search: /{locale-transport}/{locale-flights-from}/{origin}/{outbound_date}?{query_params}
 */
export const buildFlightSearchUrl = (
  searchParams: InternalGCSearchParams,
  strings: LocalisedURLStrings,
): string | null => {
  const firstLeg = searchParams.legs?.[0];

  if (!firstLeg) {
    logger.logWarn({
      component: 'FlightsSearchControlsGCWrapper',
      message: 'No flight leg found for parallel flight search',
    });
    return null;
  }

  const { date, destination, origin, return_date: returnDate } = firstLeg;

  if (!origin?.id || !date) {
    logger.logWarn({
      component: 'FlightsSearchControlsGCWrapper',
      message: 'Missing required flight search parameters for parallel search',
    });
    return null;
  }

  try {
    const queryParams = buildFlightQueryParams(
      searchParams,
      returnDate ?? undefined,
    );

    // "Everywhere" search - no specific destination
    if (!hasDestination(destination)) {
      const path = `/${strings.ktxtURLTransport}/${strings.ktxtURLFlightsFrom}/${origin.id.toLowerCase()}/${formatFlightDate(date as string)}/`;
      return `${path}?${queryParams.join('&')}`;
    }

    if (!destination?.id) {
      logger.logWarn({
        component: 'FlightsSearchControlsGCWrapper',
        message: 'Missing destination id for flight search URL',
      });
      return null;
    }

    const path = `/${strings.ktxtURLTransport}/${strings.ktxtURLFlights}/${origin.id.toLowerCase()}/${destination.id.toLowerCase()}/${formatFlightDate(date as string)}${
      returnDate ? `/${formatFlightDate(returnDate)}` : ''
    }/`;

    return `${path}?${queryParams.join('&')}`;
  } catch (error) {
    logger.logError(
      error instanceof Error
        ? error
        : new Error('Failed to build flight search URL for parallel search'),
      { component: 'FlightsSearchControlsGCWrapper' },
    );
    return null;
  }
};

/**
 * Builds a hotel search URL from flight search parameters for parallel search functionality.
 */
export const buildHotelSearchUrl = (
  searchParams: InternalGCSearchParams,
): string | null => {
  const firstLeg = searchParams.legs?.[0];

  if (!firstLeg) {
    logger.logWarn({
      component: 'FlightsSearchControlsGCWrapper',
      message: 'No flight leg found for parallel hotel search',
    });
    return null;
  }

  const { date, destination, return_date: returnDate } = firstLeg;

  if (!destination?.entityId) {
    logger.logWarn({
      component: 'FlightsSearchControlsGCWrapper',
      message:
        'Flight destination has no entityId, skipping parallel hotel search',
    });
    return null;
  }

  const checkInDate = date;
  // format() from date-fns is used here for machine-readable date formatting (URL parameter).
  // This avoids timezone issues that occur with toISOString() which converts to UTC.
  // The date is formatted in local calendar terms to ensure checkout date is stable across timezones.
  const checkOutDate =
    returnDate || format(addDays(parseISO(date as string), 1), 'yyyy-MM-dd');

  const childrenAges = searchParams.child_ages?.map(String) ?? [];

  try {
    const hotelUrl = getDayviewUrl({
      destination: { entityId: destination.entityId },
      checkInDate,
      checkOutDate,
      adults: searchParams.adults,
      rooms: 1,
      childrenAges,
    });

    const urlWithSource = `${hotelUrl}${hotelUrl.includes('?') ? '&' : '?'}source=parallel_search_flight_seo`;

    return urlWithSource;
  } catch (error) {
    logger.logError(
      error instanceof Error
        ? error
        : new Error('Failed to build hotel search URL for parallel search'),
      { component: 'FlightsSearchControlsGCWrapper' },
    );
    return null;
  }
};
